import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { GridFour, Package, Fire, UsersThree, SignOut, Truck, Receipt, ClipboardText, List, X, ChatCircleDots, BellRinging, PaperPlaneTilt } from "@phosphor-icons/react";
import { enablePushNotifications } from "@/lib/push";
import { toast } from "sonner";

const nav = [
  { to: "/", label: "Dashboard", icon: GridFour, end: true, section: "dashboard" },
  { to: "/materials", label: "Raw Materials", icon: Package, section: "materials" },
  { to: "/production", label: "Production", icon: Fire, section: "production" },
  { to: "/production-plans", label: "Production Plans", icon: ClipboardText, section: "production-plans" },
  { to: "/suppliers", label: "Suppliers", icon: Truck, section: "suppliers" },
  { to: "/purchase-orders", label: "Purchase Orders", icon: Receipt, section: "purchase-orders" },
  { to: "/dispatch", label: "Dispatch & Revenue", icon: PaperPlaneTilt, section: "dispatch" },
  { to: "/admins", label: "Admins", icon: UsersThree, section: "admins" },
  { to: "/chat", label: "Team Chat", icon: ChatCircleDots, section: null },
];

export default function Layout({ children }) {
  const { user, logout, hasPerm } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const visibleNav = nav.filter((item) => item.section === null || hasPerm(item.section, "view"));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let active = true;
    const check = () => {
      api.get("/chat/unread", { params: { since: localStorage.getItem("chat_last_read") || "" } })
        .then((r) => { if (active) setUnread(r.data.count || 0); })
        .catch(() => {});
    };
    check();
    const t = setInterval(check, 12000);
    const onRead = () => setUnread(0);
    window.addEventListener("chat-read", onRead);
    return () => { active = false; clearInterval(t); window.removeEventListener("chat-read", onRead); };
  }, []);

  const markChatRead = () => {
    localStorage.setItem("chat_last_read", new Date().toISOString());
    setUnread(0);
  };

  const enableAlerts = async () => {
    try {
      await enablePushNotifications();
      toast.success("Notifications enabled on this device");
    } catch (e) {
      toast.error(e.message || "Could not enable notifications");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 h-14 bg-[#09090B] text-white flex items-center justify-between px-4 border-b border-zinc-800">
        <div className="font-heading font-black text-lg tracking-tighter leading-none">
          <span className="text-primary">VIVA</span> HOME
        </div>
        <button onClick={() => setOpen(true)} data-testid="mobile-menu-button" aria-label="Open menu" className="p-2 -mr-2 text-white">
          <List size={24} weight="bold" />
        </button>
      </div>

      {/* Drawer overlay (mobile) */}
      {open && (
        <div onClick={() => setOpen(false)} data-testid="mobile-menu-overlay" className="lg:hidden fixed inset-0 z-40 bg-black/50" />
      )}

      {/* Sidebar — slide-in drawer on mobile, fixed on desktop */}
      <aside
        className={`w-60 bg-[#09090B] text-white flex flex-col border-r border-zinc-800 fixed top-0 left-0 h-screen z-50 transform transition-transform duration-200 lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}
      >
        <div className="px-6 py-6 border-b border-zinc-800 flex items-start justify-between">
          <div>
            <div className="font-heading font-black text-xl tracking-tighter leading-none">
              <span className="text-primary">VIVA</span>
            </div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mt-1">Home Appliances · Raw Material Mgmt</div>
          </div>
          <button onClick={() => setOpen(false)} aria-label="Close menu" data-testid="mobile-menu-close" className="lg:hidden p-1 text-zinc-400 hover:text-white">
            <X size={20} weight="bold" />
          </button>
        </div>
        <nav className="flex-1 py-4 overflow-y-auto">
          {visibleNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={() => { setOpen(false); if (item.to === "/chat") markChatRead(); }}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-3 text-sm border-l-2 transition-colors duration-100 ${
                  isActive
                    ? "bg-zinc-900 border-primary text-white"
                    : "border-transparent text-zinc-400 hover:text-white hover:bg-zinc-900/60"
                }`
              }
            >
              <item.icon size={18} weight="bold" />
              <span className="uppercase tracking-wide text-xs font-semibold">{item.label}</span>
              {item.to === "/chat" && unread > 0 && (
                <span data-testid="chat-unread-badge" className="ml-auto min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-primary text-white text-[10px] font-bold">
                  {unread > 99 ? "99+" : unread}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-zinc-800 p-4">
          <div className="text-xs text-zinc-400 mb-1 truncate" data-testid="sidebar-user-name">{user?.name}</div>
          <div className="text-[11px] text-zinc-600 mb-3 truncate">{user?.email}</div>
          <button
            onClick={enableAlerts}
            data-testid="enable-alerts-button"
            className="flex items-center gap-2 text-xs uppercase tracking-wide font-semibold text-zinc-400 hover:text-primary transition-colors duration-100 mb-3"
          >
            <BellRinging size={16} weight="bold" /> Enable Alerts
          </button>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="flex items-center gap-2 text-xs uppercase tracking-wide font-semibold text-zinc-400 hover:text-primary transition-colors duration-100"
          >
            <SignOut size={16} weight="bold" /> Sign out
          </button>
        </div>
      </aside>

      {/* Content */}
      <main className="lg:ml-60 min-h-screen pt-14 lg:pt-0">{children}</main>
    </div>
  );
}
